<script setup lang="ts">
import { useI18n } from "vue-i18n";
import TournamentPlayerCard from "./TournamentPlayerCard.vue";
import RoundFinishButton from "./RoundFinishButton.vue";
import type { TournamentPlayer, TournamentMatch, TournamentRound } from "@interfaces/tournament_type";

type Player = TournamentPlayer;
type Match = TournamentMatch;
type RoundInfo = TournamentRound;

interface Props {
  eventId: number;
  isTournamentStarted: boolean;
  isTournamentFinished: boolean;
  isUpdatingPairings: boolean;
  actionLoading: boolean;
  draggedPlayer?: Player;
  matchLoadingStates: Record<number, boolean>;
  showPreviousRounds: Record<number, boolean>;
  matchesByRound: Record<number, Match[]>;
  playerRosters: Record<number, any[]>;
  getRoundInfo: (round: number) => RoundInfo | null;
  getCompletedMatchesCount: (round: number) => number;
  isRoundActive: (round: number) => boolean;
  isMatchTie: (match: Match) => boolean;
  totalRounds: number;
}

interface Emits {
  (e: 'player-drag-start', player: Player, matchId: number): void;
  (e: 'player-drop', player: Player, matchId: number): void;
  (e: 'finish-match', matchId: number): void;
  (e: 'advance-to-next-round'): void;
  (e: 'toggle-round-visibility', round: number): void;
  (e: 'update-match-victory-points', matchId: number, playerIndex: 1 | 2, value: number): void;
  (e: 'update-match-models-cost', matchId: number, playerIndex: 1 | 2, value: number): void;
  (e: 'replay-round', round: number): void;
  (e: 'replay-match', matchId: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();

const toRoundNumber = (round: string | number): number => Number(round);

// Helper function to determine winner based on victory points only
const getMatchWinner = (match: Match): Player | null => {
  if (!match.completed || props.isMatchTie(match)) return null;

  const player1VictoryPoints = match.player1_victory_points || 0;
  const player2VictoryPoints = match.player2_victory_points || 0;

  // Determine winner: highest victory points only
  // If victory points are equal, it's a tie (returns null)
  if (player1VictoryPoints > player2VictoryPoints) {
    return match.player1;
  } else if (player2VictoryPoints > player1VictoryPoints) {
    return match.player2;
  } else {
    // Tie in victory points - always a tie, regardless of models cost
    return null;
  }
};

</script>

<template>
  <!-- Tournament Matches -->
  <div v-if="isTournamentStarted" class="tournament-matches">
    <div v-if="!isTournamentFinished" class="drag-instructions">
      <i class="bi bi-arrow-left-right"></i>
      <span>{{ t("tournament.matches.drag_instructions") }}</span>
      <span v-if="isUpdatingPairings" class="updating-indicator">
        <i class="bi bi-arrow-clockwise"></i>
        {{ t("tournament.matches.saving") }}
      </span>
    </div>
    <div
      v-for="(matches, round) in matchesByRound"
      :key="round"
      class="round-section"
      :class="{ 'previous-round': !isRoundActive(toRoundNumber(round)) }"
    >
      <!-- Current Round Header -->
      <div v-if="isRoundActive(toRoundNumber(round))" class="round-header">
        <h4 class="round-title">{{ t("tournament.round") }} {{ round }}</h4>
        <div v-if="getRoundInfo(toRoundNumber(round))" class="round-details">
          <div v-if="getRoundInfo(toRoundNumber(round))?.map" class="round-map">
            <span class="detail-label">{{ t("tournament.matches.map_label") }}</span>
            <span class="detail-value">{{ getRoundInfo(toRoundNumber(round))?.map?.name }}</span>
            <img
              v-if="getRoundInfo(toRoundNumber(round))?.map?.image_url"
              :src="getRoundInfo(toRoundNumber(round))?.map?.image_url"
              :alt="getRoundInfo(toRoundNumber(round))?.map?.name"
              class="map-thumbnail"
            />
          </div>
          <div v-if="getRoundInfo(toRoundNumber(round))?.scenario" class="round-scenario">
            <span class="detail-label">{{ t("tournament.matches.scenario_label") }}</span>
            <span class="detail-value">{{
              getRoundInfo(toRoundNumber(round))?.scenario?.name
            }}</span>
            <div
              v-if="getRoundInfo(toRoundNumber(round))?.scenario?.description"
              class="scenario-description"
            >
              {{ getRoundInfo(toRoundNumber(round))?.scenario?.description }}
            </div>
          </div>
        </div>
      </div>

      <!-- Previous Round Header with Dropdown -->
      <div v-if="!isRoundActive(toRoundNumber(round))" class="previous-round-header">
        <button
          @click="emit('toggle-round-visibility', toRoundNumber(round))"
          class="btn btn-dropdown"
          type="button"
          :class="{ 'expanded': showPreviousRounds[toRoundNumber(round)] }"
        >
          <i class="bi" :class="showPreviousRounds[toRoundNumber(round)] ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
          {{ t("tournament.matches.round_results", { round: toRoundNumber(round) }) }}
          <span class="round-status">({{ t("tournament.matches.completed_count", { completed: getCompletedMatchesCount(toRoundNumber(round)), total: matches.length }) }})</span>
          <span v-if="showPreviousRounds[toRoundNumber(round)]" class="collapse-text">{{ t("tournament.matches.collapse") }}</span>
        </button>
      </div>

      <div
        v-if="isRoundActive(toRoundNumber(round)) || (!isRoundActive(toRoundNumber(round)) && showPreviousRounds[toRoundNumber(round)])"
        class="matches-grid"
        :class="{ 'previous-round-matches': !isRoundActive(toRoundNumber(round)) }"
      >
        <div
          v-for="match in matches"
          :key="match.id"
          class="match-card"
          :class="{ completed: match.completed }"
        >
          <div class="match-header">
            <span class="match-number">{{ t("tournament.matches.match_number", { number: match.match_number }) }}</span>
            <span v-if="match.completed" class="match-status completed"
              >{{ t("tournament.matches.status.completed") }}</span
            >
            <span v-else class="match-status pending">{{ t("tournament.matches.status.pending") }}</span>
          </div>

          <div class="match-players-container">
            <div class="player-side">
              <TournamentPlayerCard
                :player="match.player1"
                :rosters="match.player1_rosters"
                :active-roster-index="match.player1_active_roster_index"
                :match-id="match.id"
                :event-id="eventId"
                :victory-points="match.player1_victory_points || 0"
                :models-cost="match.player1_models_cost || 0"
                :is-winner="match.winner?.id === match.player1.id && !isMatchTie(match)"
                :is-loser="match.loser?.id === match.player1.id && !isMatchTie(match)"
                :is-tie="isMatchTie(match)"
                :is-completed="match.completed"
                :is-draggable="!match.completed && !isUpdatingPairings"
                :is-drop-zone="!match.completed && !isUpdatingPairings"
                :is-current-opponent="draggedPlayer?.id === match.player2.id"
                :compact="true"
                @update:victory-points="
                  (value) => emit('update-match-victory-points', match.id, 1, value)
                "
                @update:models-cost="(value) => emit('update-match-models-cost', match.id, 1, value)"
                @dragstart="(player) => emit('player-drag-start', player, match.id)"
                @drop="(player) => emit('player-drop', player, match.id)"
              />
            </div>

            <div class="match-divider">
              <div class="vs-text">VS</div>
            </div>

            <div class="player-side">
              <TournamentPlayerCard
                :player="match.player2"
                :rosters="match.player2_rosters"
                :active-roster-index="match.player2_active_roster_index"
                :match-id="match.id"
                :event-id="eventId"
                :victory-points="match.player2_victory_points || 0"
                :models-cost="match.player2_models_cost || 0"
                :is-winner="match.winner?.id === match.player2.id && !isMatchTie(match)"
                :is-loser="match.loser?.id === match.player2.id && !isMatchTie(match)"
                :is-tie="isMatchTie(match)"
                :is-completed="match.completed"
                :is-draggable="!match.completed && !isUpdatingPairings"
                :is-drop-zone="!match.completed && !isUpdatingPairings"
                :is-current-opponent="draggedPlayer?.id === match.player1.id"
                :compact="true"
                @update:victory-points="
                  (value) => emit('update-match-victory-points', match.id, 2, value)
                "
                @update:models-cost="(value) => emit('update-match-models-cost', match.id, 2, value)"
                @dragstart="(player) => emit('player-drag-start', player, match.id)"
                @drop="(player) => emit('player-drop', player, match.id)"
              />
            </div>
          </div>

          <div v-if="!match.completed" class="match-actions">
            <div class="match-buttons">
              <button
                @click="emit('finish-match', match.id)"
                class="btn btn-small btn-success"
                type="button"
                :disabled="matchLoadingStates[match.id]"
              >
                <span v-if="matchLoadingStates[match.id]" class="loading-spinner"
                  >⏳</span
                >
                {{
                  matchLoadingStates[match.id]
                    ? t("tournament.matches.finishing")
                    : t("tournament.matches.finish_match")
                }}
              </button>
              <div class="match-info">
                <span class="info-text">{{ t("tournament.matches.winner_by_victory_points_only") }}</span>
              </div>
            </div>
          </div>

          <div v-else class="match-result">
            <div v-if="isMatchTie(match)" class="tie-result">
              <i class="bi bi-dash-circle"></i>
              {{ t("tournament.matches.tie") }}
            </div>
            <div v-else>
              <div class="winner">
                <i class="bi bi-trophy"></i>
                {{ t("tournament.matches.winner") }} {{ getMatchWinner(match)?.name }}
              </div>
            </div>
            <div class="match-replay-action">
              <button
                @click="emit('replay-match', match.id)"
                class="btn btn-small btn-replay-match"
                type="button"
                :disabled="actionLoading || matchLoadingStates[match.id]"
                :title="t('tournament.matches.unlock_match_title')"
              >
                <i class="bi bi-arrow-counterclockwise"></i>
                {{ actionLoading || matchLoadingStates[match.id] ? t("tournament.matches.unlocking") : t("tournament.matches.replay_match") }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Round Finish Button -->
      <RoundFinishButton
        :round="Number(round)"
        :matches="matches"
        :action-loading="actionLoading"
        :is-round-active="isRoundActive"
        :get-completed-matches-count="getCompletedMatchesCount"
        :is-last-round="Number(round) >= totalRounds"
        @finish-round="emit('advance-to-next-round')"
      />

      <!-- Replay Round Button (for completed rounds) -->
      <div v-if="!isRoundActive(toRoundNumber(round)) && matches.every(m => m.completed)" class="replay-round-container">
        <button
          @click="emit('replay-round', toRoundNumber(round))"
          class="btn btn-replay"
          type="button"
          :disabled="actionLoading"
          :title="t('tournament.matches.unlock_round_title')"
        >
          <i class="bi bi-arrow-counterclockwise"></i>
          {{ actionLoading ? t("tournament.matches.unlocking") : t("tournament.matches.replay_round") }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tournament-matches {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.drag-instructions {
  background-color: rgba(33, 150, 243, 0.1);
  border: 1px solid var(--info-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-sm) var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--info-text);
  font-size: var(--font-size-sm);
  font-weight: 500;

  i {
    font-size: var(--font-size-base);
  }

  .updating-indicator {
    margin-left: auto;
    color: var(--warning-color);
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);

    i {
      animation: spin 1s linear infinite;
    }
  }
}

.updating-indicator {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--primary-color);
  font-weight: 500;

  i {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.round-section {
  margin-bottom: var(--spacing-xl);
  background-color: var(--bg-white);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
  overflow: hidden;
}

.round-header {
  background-color: var(--icon-bg);
  color: var(--icon-text);
  padding: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

.round-title {
  color: var(--text-primary);
  font-size: var(--font-size-lg);
  font-weight: bold;
  margin-bottom: var(--spacing-md);
}

.round-details {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
  font-size: var(--font-size-sm);
}

.round-map,
.round-scenario {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: var(--gradient-secondary);
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
}

.detail-label {
  font-weight: 600;
  color: var(--text-secondary);
}

.detail-value {
  color: var(--text-primary);
  font-weight: 500;
}

.map-thumbnail {
  width: 50px;
  height: 35px;
  object-fit: cover;
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
}

.scenario-description {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin-top: var(--spacing-xs);
  font-style: italic;
}

// Previous round styles
.previous-round {
  margin-bottom: var(--spacing-lg);
}

.previous-round-header {
  margin-bottom: var(--spacing-md);
}

.btn-dropdown {
  background: var(--gradient-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: var(--transition-normal);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  font-weight: 500;
  width: 100%;
  justify-content: flex-start;

  &:hover {
    background: var(--gradient-secondary-hover);
    border-color: var(--hover-bg);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px var(--shadow-light);
  }

  &:active {
    transform: translateY(0);
  }

  &.expanded {
    background: var(--gradient-primary);
    color: var(--light-base);
    border-color: var(--primary-color);
  }

  i {
    font-size: 14px;
    transition: transform 0.2s ease;
  }

  .round-status {
    margin-left: auto;
    font-size: var(--font-size-xs);
    opacity: 0.8;
    font-weight: normal;
  }

  .collapse-text {
    margin-left: var(--spacing-sm);
    font-size: var(--font-size-xs);
    opacity: 0.7;
    font-style: italic;
  }
}

.previous-round-matches {
  animation: slideDown 0.3s ease-out;
  border-left: 3px solid var(--border-color);
  padding-left: var(--spacing-md);
  margin-top: var(--spacing-sm);
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 1000px;
    transform: translateY(0);
  }
}

.matches-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--spacing-lg);
  padding: 15px;
}

.match-card {
  background: var(--primary-bg);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-lg);
  transition: var(--transition-normal);
  min-width: 330px;

  &.completed {
    border-color: var(--success-color);
    background: var(--success-light);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--shadow-light);
  }
}

.match-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.match-number {
  font-weight: bold;
  color: #60451f;
  font-size: 14px;
}

.match-status {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;

  &.completed {
    background-color: #4caf50;
    color: white;
  }

  &.pending {
    background-color: #ff9800;
    color: white;
  }
}

.match-players-container {
  display: flex;
  align-items: stretch;
  gap: 0;
  margin-bottom: 10px;
  position: relative;
  min-height: 120px;
}

.player-side {
  flex: 1;
  min-width: 0;
  padding: 12px;
  margin: 0 2px;
}

.match-divider {
  width: 3px;
  background: linear-gradient(to bottom, #f44336, #d32f2f);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
}

.vs-text {
  background-color: white;
  color: #f44336;
  font-weight: bold;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  border: 2px solid #f44336;
  transform: rotate(-90deg);
  white-space: nowrap;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-90deg);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.match-actions {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.match-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn {
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: scale(0.98);
  }
}

.loading-spinner {
  animation: spin 1s linear infinite;
}

.match-info {
  background: var(--gradient-secondary);
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
}

.info-text {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  line-height: 1.4;
}

.match-result {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-color);
  text-align: center;
  font-size: var(--font-size-sm);
}

.tie-result {
  color: var(--warning-color);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

.winner {
  color: var(--success-color);
  font-weight: 600;
  margin-bottom: var(--spacing-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

.loser {
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
}

// Mobile responsive
@media (max-width: 768px) {
  .matches-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .match-card {
    min-width: auto;
    padding: 12px;
  }

  .match-players-container {
    flex-direction: column;
    min-height: auto;
    gap: 10px;
  }

  .player-side {
    padding: 8px;
    width: 100%;
  }

  .match-divider {
    width: 100%;
    height: 3px;
    transform: rotate(0deg);
  }

  .round-details {
    flex-direction: column;
    gap: var(--spacing-sm);
    font-size: var(--font-size-xs);
  }

  .round-map,
  .round-scenario {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .map-thumbnail {
    width: 35px;
    height: 25px;
  }

  .scenario-description {
    font-size: 11px;
    margin-top: 4px;
  }

  .match-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .match-number {
    font-size: 12px;
  }

  .match-status {
    font-size: 9px;
    padding: 1px 4px;
  }

  .match-actions {
    margin-top: 8px;
    padding-top: 8px;
  }

  .match-buttons {
    gap: 6px;
  }

  .match-info {
    padding: 8px;
    font-size: 10px;
  }

  .match-result {
    margin-top: 8px;
    padding-top: 8px;
    font-size: 11px;
  }

  .drag-instructions {
    flex-direction: column;
    gap: 8px;
    text-align: center;
    padding: 10px;
    font-size: 12px;
  }

  .updating-indicator {
    margin-left: 0;
    margin-top: 4px;
  }

  .btn-dropdown {
    padding: var(--spacing-sm);
    font-size: var(--font-size-xs);

    .round-status {
      font-size: 10px;
    }
  }

  .previous-round-matches {
    padding-left: var(--spacing-sm);
  }
}

.replay-round-container {
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-color);
}

.btn-replay {
  background-color: var(--warning-color, #ff9800);
  color: white;
  border: 2px solid var(--warning-color, #ff9800);
  font-weight: bold;
  transition: var(--transition-fast);
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);

  &:hover:not(:disabled) {
    background-color: var(--warning-color-dark, #f57c00);
    border-color: var(--warning-color-dark, #f57c00);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px var(--shadow-light);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px var(--shadow-light);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  i {
    font-size: var(--font-size-base);
  }
}

.match-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-color);

  .match-replay-action {
    margin-top: var(--spacing-sm);
  }

  .btn-replay-match {
    background-color: var(--warning-color, #ff9800);
    color: white;
    border: 2px solid var(--warning-color, #ff9800);
    font-weight: bold;
    transition: var(--transition-fast);
    padding: 8px 16px;
    border-radius: var(--border-radius-md);
    font-size: var(--font-size-xs);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);

    &:hover:not(:disabled) {
      background-color: var(--warning-color-dark, #f57c00);
      border-color: var(--warning-color-dark, #f57c00);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px var(--shadow-light);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 1px 2px var(--shadow-light);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }

    i {
      font-size: var(--font-size-sm);
    }
  }
}

@media (max-width: 480px) {
  .match-card {
    padding: 10px;
  }

  .player-side {
    padding: 6px;
  }

  .btn {
    padding: 10px 14px;
    font-size: 14px;
  }
}
</style>
